<template>
  <section class="text-white py-11 px-6">
    <div class="max-w-6xl mx-auto text-center">
      <div data-aos="fade-down">
        <h1 class="text-4xl lg:text-7xl font-bold text-[#e4ac3b] uppercase drop-shadow-[2px_2px_5px_rgba(228,172,59,0.7)]">
          Products
        </h1>
        <div class="w-80 h-px border border-[#e4ac3b] mx-auto mt-2 drop-shadow-[2px_2px_5px_rgba(228,172,59,0.7)]"></div>
      </div>
    </div>

    <!-- Carousel Wrapper -->
    <div class="carousel-wrapper" data-aos="fade-up" data-aos-delay="200">
      <!-- Left Navigation Button -->
      <button
        @click="prevProduct"
        class="carousel-nav carousel-nav-left"
        aria-label="Previous product"
        :disabled="products.length <= 1"
      >
        <span class="arrow">&lt;</span>
      </button>

      <!-- Carousel Container -->
      <div class="carousel-container">
        <div 
          class="carousel-track" 
          :style="{ transform: `translateX(calc(-${currentIndex * 100}% - ${currentIndex * 32}px))` }"
        >
          <article
            v-for="(product, index) in products"
            :key="product.id"
            class="product-card"
            :class="{ 
              'is-active': index === currentIndex,
              'is-adjacent': Math.abs(index - currentIndex) === 1 || 
                            (currentIndex === 0 && index === products.length - 1) ||
                            (currentIndex === products.length - 1 && index === 0)
            }"
          >
            <div class="product-content">
              <div 
                class="product-image-container"
                :class="{ 'order-2 md:order-1': product.imageOrder === 'right' }"
              >
                <img 
                  :src="product.image" 
                  :alt="product.alt" 
                  class="product-image"
                  loading="lazy"
                >
              </div>
              <div 
                class="product-text"
                :class="{ 'order-1 md:order-2': product.imageOrder === 'right' }"
              >
                <h2 class="text-5xl font-bold text-gold">{{ product.title }}</h2>
                <p class="text-gray-300 mt-4">
                  {{ product.description }}
                </p>
                <a 
                  v-if="product.link"
                  :href="product.link" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  class="mt-6 glow-button"
                  :aria-label="`Visit ${product.title} website`"
                >
                  Visit Site
                </a>
                <button 
                  v-else
                  class="mt-6 glow-button disabled" 
                  disabled
                >
                  Coming Soon...
                </button>
              </div>
            </div>
          </article>
        </div>
      </div>

      <!-- Right Navigation Button -->
      <button
        @click="nextProduct"
        class="carousel-nav carousel-nav-right"
        aria-label="Next product"
        :disabled="products.length <= 1"
      >
        <span class="arrow">&gt;</span>
      </button>
    </div>

    <!-- Carousel Indicators -->
    <div class="carousel-indicators" v-if="products.length > 1">
      <button
        v-for="(_, index) in products"
        :key="index"
        @click="goToProduct(index)"
        class="indicator"
        :class="{ 'active': index === currentIndex }"
        :aria-label="`Go to product ${index + 1}`"
      />
    </div>
  </section>
</template>

<script setup>
import { ref } from 'vue';
import { useAos } from '../hooks/useAos';
import ebooksImg from '../assets/images/ebooks.webp';
import exodusImg from '../assets/images/exodus.webp';

const currentIndex = ref(0);

const products = ref([
  {
    id: 1,
    title: 'Wisdom eBooks Club',
    description: 'Access our extensive wisdom eBook library and eStudy Bible software. Join us today and carry your faith in your pocket wherever you go.',
    image: ebooksImg,
    alt: 'Wisdom eBooks Club - Digital Library and Study Bible Software',
    link: 'https://wisdomebooksclub.com',
    imageOrder: 'left'
  },
  {
    id: 2,
    title: 'EXODUS',
    description: 'EXODUS is on its way! A powerful platform dedicated to empowering and uplifting the community through faith, knowledge, and connection.',
    image: exodusImg,
    alt: 'EXODUS Platform - Faith and Community Empowerment',
    link: null,
    imageOrder: 'right'
  }
  // Add more products here - carousel will scale automatically
]);

// Wrap-around navigation
const nextProduct = () => {
  if (products.value.length <= 1) return;
  currentIndex.value = (currentIndex.value + 1) % products.value.length;
};

const prevProduct = () => {
  if (products.value.length <= 1) return;
  currentIndex.value = currentIndex.value === 0 
    ? products.value.length - 1 
    : currentIndex.value - 1;
};

const goToProduct = (index) => {
  currentIndex.value = index;
};

useAos();
</script>

<style scoped>
.text-gold {
  color: #C0954D;
}

/* Carousel Wrapper */
.carousel-wrapper {
  position: relative;
  max-width: 1200px;
  margin: 3rem auto;
  padding: 0 80px;
}

/* Carousel Container with visible overflow */
.carousel-container {
  overflow: visible;
  position: relative;
}

.carousel-track {
  display: flex;
  gap: 2rem;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Product Cards */
.product-card {
  flex: 0 0 100%;
  transition: all 0.5s ease;
  opacity: 0.3;
  transform: scale(0.85);
  filter: blur(2px);
  pointer-events: none;
}

.product-card.is-active {
  opacity: 1;
  transform: scale(1);
  filter: blur(0);
  z-index: 2;
  pointer-events: auto;
}

.product-card.is-adjacent {
  opacity: 0.5;
  transform: scale(0.9);
  filter: blur(1px);
  z-index: 1;
}

.product-content {
  background-color: #131313;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  align-items: center;
  min-height: 24rem;
  transition: all 0.3s ease-in-out;
}

.product-card.is-active .product-content:hover {
  box-shadow: 0 0 30px rgba(228, 172, 59, 0.6);
  transform: translateY(-5px);
}

.product-image-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.product-image {
  max-height: 250px;
  max-width: 100%;
  object-fit: contain;
  transition: all 0.3s ease-in-out;
}

.product-card.is-active .product-content:hover .product-image {
  filter: drop-shadow(0 0 15px #e4ac3b);
  transform: scale(1.05);
}

.product-text {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.carousel-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 60px;
  height: 60px;
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  font-size: 48px;
  line-height: 1;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
  padding: 0;
  user-select: none;
}

.carousel-nav .arrow {
  display: inline-block;
  transform: translateY(-5px);
  list-style: 1;
}

.carousel-nav:hover:not(:disabled) {
  background: linear-gradient(135deg, #d4a556 0%, #e4ac3b 100%);
  transform: translateY(-50%) scale(1.15);
}

.carousel-nav:active:not(:disabled) {
  transform: translateY(-50%) scale(0.95);
}

.carousel-nav:disabled {
  opacity: 0.2;
  cursor: not-allowed;
  background: rgba(60, 60, 60, 0.3);
  border-color: rgba(255, 255, 255, 0.1);
  box-shadow: none;
}

.carousel-nav-left {
  left: 0;
}

.carousel-nav-right {
  right: 0;
}

/* Carousel Indicators */
.carousel-indicators {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 2rem;
}

.indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: rgba(192, 149, 77, 0.3);
  border: 2px solid rgba(192, 149, 77, 0.5);
  cursor: pointer;
  transition: all 0.3s ease-in-out;
}

.indicator.active {
  background: #C0954D;
  transform: scale(1.4);
  box-shadow: 0 0 10px rgba(192, 149, 77, 0.6);
}

.indicator:hover {
  background: rgba(192, 149, 77, 0.7);
  transform: scale(1.2);
}

.glow-button {
  background-color: #C0954D;
  color: black;
  padding: 12px 24px;
  font-weight: 600;
  border-radius: 6px;
  transition: all 0.3s ease-in-out;
  cursor: pointer;
  text-align: center;
  display: inline-block;
  width: 170px;
  border: none;
  text-decoration: none;
  font-size: 16px;
}

.glow-button:hover {
  box-shadow: 0 0 20px rgba(192, 149, 77, 0.9);
  transform: scale(1.08);
  background-color: #d4a556;
}

.glow-button:active {
  transform: scale(0.98);
  box-shadow: 0 0 10px rgba(192, 149, 77, 0.7);
}

.disabled {
  background-color: #555;
  cursor: not-allowed;
  box-shadow: none;
}

.disabled:hover {
  transform: none;
  box-shadow: none;
  background-color: #555;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .carousel-wrapper {
    padding: 0 60px;
  }
}

@media (max-width: 768px) {
  .carousel-wrapper {
    padding: 0 50px;
  }

  .product-content {
    grid-template-columns: 1fr;
    padding: 1.5rem;
  }

  .product-text h2 {
    font-size: 2rem;
  }

  .carousel-nav {
    width: 50px;
    height: 50px;
    font-size: 40px;
  }

  .product-card.is-adjacent {
    opacity: 0;
  }
}
</style>